import React, { useState, useEffect } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import PDFViewer from '../components/puzzle-pdf/PDFViewer';
import PuzzleWorkspace from '../components/puzzle-pdf/PuzzleWorkspace';
import { useLanguage } from '../contexts/LanguageContext';
import '../puzzleApp.css';

function PuzzlePdf() {
  const { t } = useLanguage();
  const [selectedAreas, setSelectedAreas] = useState([]);
  const [activeTab, setActiveTab] = useState('pdf');
  const [pdfUrl, setPdfUrl] = useState('');
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfSource, setPdfSource] = useState('file'); // 'file' or 'url'
  const [urlParamLoaded, setUrlParamLoaded] = useState(false);

  // Fallback URLs to try if the first one fails
  const fallbackUrls = [
    'https://app.rowcounterapp.com/generated_pdfs/pattern_qqqvmakuktnxalcj_120_newborn9.pdf',
    'https://cors-anywhere.herokuapp.com/https://app.rowcounterapp.com/generated_pdfs/pattern_qqqvmakuktnxalcj_120_newborn9.pdf',
    'https://mozilla.github.io/pdf.js/web/compressed.tracemonkey-pldi-09.pdf' // Test PDF
  ];

  // Helper function to validate PDF URLs
  const isValidPdfUrl = (url) => {
    try {
      new URL(url);
      return url.toLowerCase().includes('.pdf') || url.includes('pdf');
    } catch {
      return false;
    }
  };

  // Check for URL parameters on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);

    // Function to process base64 PDF data from React Native
    const processRNPdfData = () => {
      if (!window.__RN_PDF_BASE64__) {
        console.log('⏳ Waiting for __RN_PDF_BASE64__ to be set...');
        return false;
      }

      console.log('📱 Android base64 PDF data detected, length:', window.__RN_PDF_BASE64__.length);

      try {
        // Convert base64 to blob
        const binaryString = atob(window.__RN_PDF_BASE64__);
        console.log('📱 Decoded base64, binary length:', binaryString.length);

        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }

        const blob = new Blob([bytes], { type: 'application/pdf' });
        console.log('📱 Created blob from base64, size:', blob.size);

        // Create blob URL for PDF.js
        const blobUrl = URL.createObjectURL(blob);
        console.log('📱 Created blob URL:', blobUrl);

        setPdfUrl(blobUrl);
        setPdfSource('file');
        setPdfFile(new File([blob], 'pattern.pdf', { type: 'application/pdf' }));
        setUrlParamLoaded(true);
        setActiveTab('pdf');
        console.log('✅ PDF loaded from Android base64 injection');

        // Clean up global variable
        delete window.__RN_PDF_BASE64__;
        return true;
      } catch (error) {
        console.error('❌ Error processing base64 PDF:', error);
        alert('Failed to process PDF data: ' + error.message);
        return false;
      }
    };

    // Check for base64 PDF data injected by React Native (Android)
    if (window.isReactNativeWebView) {
      console.log('🔍 In React Native WebView, checking for PDF data...');

      // Try immediately
      if (processRNPdfData()) {
        return; // Success, don't check other parameters
      }

      // If not available yet, poll for it (injectedJavaScript runs after page load)
      let attempts = 0;
      const checkInterval = setInterval(() => {
        attempts++;
        console.log('🔍 Polling attempt', attempts, 'for __RN_PDF_BASE64__');

        if (processRNPdfData()) {
          clearInterval(checkInterval);
          console.log('✅ Found PDF data after', attempts, 'attempts');
        } else if (attempts > 20) {
          // Stop after 2 seconds (20 * 100ms)
          clearInterval(checkInterval);
          console.log('⏹️ Stopped polling, checking URL parameters instead');

          // If we never got the data, check URL parameters
          checkUrlParameters();
        }
      }, 100);

      return () => clearInterval(checkInterval);
    } else {
      checkUrlParameters();
    }

    // Function to check URL parameters for PDF
    function checkUrlParameters() {
      // Support multiple parameter names: pdf, pdfUrl, url
      const pdfParam = urlParams.get('pdf') ||
                      urlParams.get('pdfUrl') ||
                      urlParams.get('url');

      if (pdfParam) {
        console.log('PDF URL parameter detected:', pdfParam);

        // Validate the URL before setting it
        if (isValidPdfUrl(pdfParam)) {
          setPdfUrl(pdfParam);
          setPdfSource('url');
          setPdfFile(null);
          setUrlParamLoaded(true);
          // Switch to the PDF tab automatically
          setActiveTab('pdf');
          console.log('✅ PDF URL loaded from parameter');
        } else {
          console.warn('⚠️ Invalid PDF URL in parameter:', pdfParam);
          // Could show a user-friendly error message here if needed
        }
      }
    }

    // Set up handler for React Native PDF upload
    window.handlePdfUploadFromReactNative = (file) => {
      console.log('📱 Handling PDF upload from React Native:', file);
      console.log('📱 File name:', file.name, 'type:', file.type, 'size:', file.size);
      if (file && file.type === 'application/pdf') {
        setPdfFile(file);
        setPdfSource('file');
        // Create object URL for the file
        const fileUrl = URL.createObjectURL(file);
        console.log('📱 Created object URL:', fileUrl);
        setPdfUrl(fileUrl);
        // Switch to the PDF tab automatically
        setActiveTab('pdf');
        console.log('✅ PDF loaded from React Native');
      } else {
        console.error('❌ Invalid file received from React Native', file);
      }
    };

    // Signal to React Native that we're ready
    console.log('🟢 handlePdfUploadFromReactNative is now available');
    console.log('🔍 window.isReactNativeWebView:', window.isReactNativeWebView);
    console.log('🔍 window.ReactNativeWebView:', typeof window.ReactNativeWebView);

    // If we're in a WebView, notify that we're ready
    if (window.isReactNativeWebView && window.ReactNativeWebView) {
      console.log('📱 Notifying React Native that handler is ready');
      try {
        window.ReactNativeWebView.postMessage(JSON.stringify({
          type: 'WEB_APP_READY'
        }));
        console.log('✅ WEB_APP_READY message sent successfully');
      } catch (error) {
        console.error('❌ Error sending WEB_APP_READY message:', error);
      }
    } else {
      console.log('⚠️ Not in React Native WebView or ReactNativeWebView not available');
    }

    // Cleanup
    return () => {
      delete window.handlePdfUploadFromReactNative;
    };
  }, []); // Empty dependency array means this runs once on mount

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'application/pdf') {
      setPdfFile(file);
      setPdfSource('file');
      // Create object URL for the file
      const fileUrl = URL.createObjectURL(file);
      setPdfUrl(fileUrl);
    } else {
      alert('Please select a PDF file');
    }
  };

  const handleUrlChange = (url) => {
    setPdfUrl(url);
    setPdfSource('url');
    setPdfFile(null);
    setUrlParamLoaded(false); // Reset parameter loaded state for manual changes
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="App">
        <header className="app-header">
          <h1>{t('appTitle')}</h1>
          <div className="tab-navigation">
            <button
              className={`tab ${activeTab === 'pdf' ? 'active' : ''}`}
              onClick={() => setActiveTab('pdf')}
            >
              {t('tabSelectAreas')} ({selectedAreas.length})
            </button>
            <button
              className={`tab ${activeTab === 'puzzle' ? 'active' : ''}`}
              onClick={() => setActiveTab('puzzle')}
            >
              {t('tabCompose')}
            </button>
          </div>
        </header>

        <main className="app-content">
          {activeTab === 'pdf' && (
            <div className="tab-content">
              {/* Show PDF source controls only when no PDF is loaded */}
              {!pdfUrl && !pdfFile && (
                <div className="pdf-url-controls">
                  <div>
                    <label htmlFor="pdf-upload" style={{
                      display: 'inline-block',
                      padding: '12px 24px',
                      border: '1px solid #2ba5f7',
                      borderRadius: '24px',
                      fontSize: '1rem',
                      cursor: 'pointer',
                      color: '#2ba5f7',
                    }}>{t('uploadPdfFile')}</label>
                    <input
                      id="pdf-upload"
                      class="form-control"
                      placeholder="Enter email"
                      type="file"
                      accept=".pdf"
                      onChange={handleFileUpload}
                      style={{
                        display: "none"
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Show PDF viewer and related content only when PDF is loaded */}
              {(pdfUrl || pdfFile) && (
                <>
                  <PDFViewer
                    pdfUrl={pdfUrl}
                    selectedPieces={selectedAreas}
                    setSelectedPieces={setSelectedAreas}
                    onError={(error) => console.error('PDF Error:', error)}
                  />
                </>
              )}
            </div>
          )}

          {activeTab === 'puzzle' && (
            <div className="tab-content">
              <PuzzleWorkspace selectedPieces={selectedAreas} setSelectedPieces={setSelectedAreas} />
            </div>
          )}
        </main>
      </div>
    </DndProvider>
  );
}

export default PuzzlePdf;
